import { Routes } from '@angular/router';
import { CanDeactivateGuard } from '../app/guards/can-deactivate.guard';
import { CanActivateGuard } from '../app/guards/can-activate.guards';
import { PublicDefaultUrlRedirectGuard } from '../app/guards/public-default-url-redirect.guard';

const config = (<any>window).ftClientConfig;

/**
 * Splits `config.map.url` (e.g. `/public/formNoNavBar/<documentId>` — the same
 * `/<accountPublicURL>/<mode>/<id>` shape the core public-login mechanism already uses) into the
 * public account slug and the document to land on, so `/map` can be sourced from one intuitive
 * config field instead of two separate ones. Parsed rather than navigated to directly, so the
 * address bar stays at the clean, stable `/map` instead of jumping to the alias path.
 * @param aliasUrl - a `/<accountPublicURL>/<mode>/<documentId>`-shaped path, or undefined
 * @returns the parsed `{ publicAccountURL, documentId }`, or an empty object if `aliasUrl` is falsy
 */
function parsePublicAlias(aliasUrl: string): { publicAccountURL?: string; documentId?: string } {
  if (!aliasUrl) {
    return {};
  }

  const [, publicAccountURL, , documentId] = aliasUrl.split('/');
  return { publicAccountURL, documentId };
}

function getAuthRoute(config): Routes {
  if (config.authentication?.disableLocalLogin === true) {
    return [];
  }

  return [{
    path: 'auth',
    loadChildren: () => import('../app/modules/auth/auth.module').then(m => m.AuthModule)
  }];
}

const authRoutes: Routes = [];
// push after initialization so the initializer is an ArrayLiteralExpression
authRoutes.push(...getAuthRoute(config));

const mapAlias = parsePublicAlias(config?.map?.url);

// only added for deployments that configure a publicDefaultURL — every other deployment's route
// table (and its lack of a catch-all) is unchanged. Must stay last: '**' has to match after everything else.
const publicDefaultUrlRoutes: Routes = [];
if (config?.publicDefaultURL) {
  publicDefaultUrlRoutes.push({
    path: '**',
    data: { defaultRoute: '/notFound' },
    loadComponent: () => import('../app/components/not-found/not-found.component').then(m => m.NotFoundComponent),
    canActivate: [PublicDefaultUrlRedirectGuard]
  });
}

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    // PublicDefaultUrlRedirectGuard always resolves a UrlTree, so DefaultComponent never actually
    // renders — it's here only because Angular requires componentless routes to declare a destination.
    loadComponent: () => import('../app/components/default/default.component').then(m => m.DefaultComponent),
    canActivate: [PublicDefaultUrlRedirectGuard]
  },
  {
    path: 'default',
    loadComponent: () => import('../app/components/default/default.component').then(m => m.DefaultComponent),
    canActivate: [CanActivateGuard]
  },
  ...authRoutes,
  {
    path: 'search',
    loadChildren: () => import('../app/modules/search/search.module').then(m => m.SearchModule)
  },
  {
    path: 'form',
    loadComponent: () => import('../app/components/top-bar/top-bar.component').then(m => m.TopBarComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('../app/components/default/default.component').then(m => m.DefaultComponent),
        pathMatch: 'full'
      },
      {
        path: ':id',
        loadComponent: () => import('../app/components/template/template-view.component').then(m => m.TemplateViewComponent),
        canDeactivate: [CanDeactivateGuard],
        canActivate: [CanActivateGuard]
      },
      {
        path: ':id/:templateId',
        loadComponent: () => import('../app/components/template/template-view.component').then(m => m.TemplateViewComponent),
        canDeactivate: [CanDeactivateGuard],
        canActivate: [CanActivateGuard]
      }
    ]
  },
  {
    path: 'formNoNavBar/:id',
    loadComponent: () => import('../app/components/template/template-view.component').then(m => m.TemplateViewComponent),
    data: { noNavBar: true },
    canDeactivate: [CanDeactivateGuard],
    canActivate: [CanActivateGuard]
  },
  {
    path: 'map',
    loadComponent: () => import('../app/components/template/template-view.component').then(m => m.TemplateViewComponent),
    data: {
      noNavBar: true,
      // falls back to the older, standalone config.map.documentId so deployments not using
      // config.map.url (e.g. the shared test-e2e.json) are unaffected
      documentId: mapAlias?.documentId ?? config?.map?.documentId,
      autoPublicLoginAccountURL: mapAlias?.publicAccountURL
    },
    canDeactivate: [CanDeactivateGuard],
    canActivate: [CanActivateGuard]
  },
  {
    path: 'map/:id',
    loadComponent: () => import('../app/components/template/template-view.component').then(m => m.TemplateViewComponent),
    data: { noNavBar: true },
    canDeactivate: [CanDeactivateGuard],
    canActivate: [CanActivateGuard]
  },
  {
    path: 'notFound',
    loadComponent: () => import('../app/components/not-found/not-found.component').then(m => m.NotFoundComponent)
  },
  {
    path: ':accountPublicURL',
    loadComponent: () => import('../app/components/public-login/public-login.component').then(m => m.PublicLoginComponent)
  },
  {
    path: ':accountPublicURL/:paramRoute/:paramQuery',
    loadComponent: () => import('../app/components/public-login/public-login.component').then(m => m.PublicLoginComponent)
  },
  {
    path: ':accountPublicURL/:paramRoute',
    loadComponent: () => import('../app/components/public-login/public-login.component').then(m => m.PublicLoginComponent)
  },
  {
    path: ':accountPublicURL/:paramRoute/:paramQuery/:override',
    loadComponent: () => import('../app/components/public-login/public-login.component').then(m => m.PublicLoginComponent)
  },
  ...publicDefaultUrlRoutes
];
